'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';

export type WorkflowStepId =
  | 'erfassen'
  | 'abgleichen'
  | 'buchen'
  | 'steuern'
  | 'auswerten'
  | 'abschliessen';

export interface WorkflowStep {
  id: WorkflowStepId;
  label: string;
  groupLabel: string;
  description: string;
  pathPrefixes: readonly string[];
  iconKey: 'inbox' | 'arrows' | 'book' | 'percent' | 'chart' | 'check';
}

export const WORKFLOW_STEPS: readonly WorkflowStep[] = [
  {
    id: 'erfassen',
    label: 'Erfassen',
    groupLabel: 'Erfassen',
    description: 'Belege und Daten einsammeln.',
    pathPrefixes: [
      '/einkauf/eingangsbelege',
      '/importe',
      '/buchhaltung/kassenbuch',
      '/einkauf/self-billing',
    ],
    iconKey: 'inbox',
  },
  {
    id: 'abgleichen',
    label: 'Abgleichen',
    groupLabel: 'Abgleichen',
    description: 'Bank, Kasse und offene Posten abstimmen.',
    pathPrefixes: [
      '/buchhaltung/bankumsaetze',
      '/buchhaltung/zahlungsabgleich',
      '/buchhaltung/offene-posten',
      '/verkauf/mahnungen',
      '/buchhaltung/zahlungen',
      '/buchhaltung/aufrechnungen',
      '/buchhaltung/umbuchungen',
    ],
    iconKey: 'arrows',
  },
  {
    id: 'buchen',
    label: 'Buchen',
    groupLabel: 'Buchen',
    description: 'Vorschläge, Journal und Konten.',
    pathPrefixes: [
      '/buchhaltung/buchungsvorschlaege',
      '/buchhaltung/journal',
      '/buchhaltung/konten',
      '/buchhaltung/bankkonten',
    ],
    iconKey: 'book',
  },
  {
    id: 'steuern',
    label: 'Steuern',
    groupLabel: 'Steuern',
    description: 'Steuerlogik und Nachweise.',
    pathPrefixes: ['/steuern', '/reports/ustva-preview'],
    iconKey: 'percent',
  },
  {
    id: 'auswerten',
    label: 'Auswerten',
    groupLabel: 'Auswerten',
    description: 'Berichte, KPIs und Forecast.',
    pathPrefixes: ['/reports', '/einstellungen/budgets'],
    iconKey: 'chart',
  },
  {
    id: 'abschliessen',
    label: 'Abschließen',
    groupLabel: 'Abschließen',
    description: 'Perioden, Monats- und Jahresabschluss.',
    pathPrefixes: ['/buchhaltung/perioden'],
    iconKey: 'check',
  },
];

const STORAGE_KEY = 'kontora-sidebar-workflow-step';

function safeGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function readStoredStep(): WorkflowStepId | null {
  if (typeof window === 'undefined') return null;
  const raw = safeGet(STORAGE_KEY);
  if (!raw) return null;
  return WORKFLOW_STEPS.some((s) => s.id === raw) ? (raw as WorkflowStepId) : null;
}

function writeStoredStep(id: WorkflowStepId): void {
  if (typeof window === 'undefined') return;
  safeSet(STORAGE_KEY, id);
  if (listeners.size > 0) {
    for (const listener of listeners) listener();
  }
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (typeof window !== 'undefined') {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) listener();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', onStorage);
    };
  }
  return () => {
    listeners.delete(listener);
  };
}

function getStoredStepSnapshot(): WorkflowStepId | null {
  return readStoredStep();
}

function getServerStoredStepSnapshot(): WorkflowStepId | null {
  return null;
}

function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname === '/') return pathname;
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function findStepByPath(pathname: string): WorkflowStep | null {
  const normalized = normalizePathname(pathname);
  if (normalized === '/' || normalized === '') return null;
  for (const step of WORKFLOW_STEPS) {
    for (const prefix of step.pathPrefixes) {
      const normPrefix = normalizePathname(prefix);
      if (normalized === normPrefix || normalized.startsWith(`${normPrefix}/`)) {
        return step;
      }
    }
  }
  return null;
}

export interface UseWorkflowStepResult {
  steps: readonly WorkflowStep[];
  activeStep: WorkflowStep | null;
  activeStepId: WorkflowStepId | null;
  setActiveStep: (id: WorkflowStepId) => void;
}

export function useWorkflowStep(): UseWorkflowStepResult {
  const pathname = usePathname();
  const storedStep = useSyncExternalStore(
    subscribe,
    getStoredStepSnapshot,
    getServerStoredStepSnapshot,
  );

  const pathStep = useMemo(() => findStepByPath(pathname ?? ''), [pathname]);

  const activeStep =
    pathStep ?? (storedStep ? (WORKFLOW_STEPS.find((s) => s.id === storedStep) ?? null) : null);

  const setActiveStep = useCallback((id: WorkflowStepId) => {
    writeStoredStep(id);
  }, []);

  return {
    steps: WORKFLOW_STEPS,
    activeStep,
    activeStepId: activeStep?.id ?? null,
    setActiveStep,
  };
}

export function isWorkflowStepId(value: unknown): value is WorkflowStepId {
  return typeof value === 'string' && WORKFLOW_STEPS.some((s) => s.id === value);
}
